import { useState } from 'react'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { BooleanToggleGroup } from '@components/BooleanToggleGroup'
import { EditCombiUitvoeringPerDiscipline } from '@components/EditCombiUitvoeringPerDiscipline'
import { EditHoofdleidingPerDiscipline } from '@components/EditHoofdleidingPerDiscipline'
import PlanningConflict from '@components/PlanningConflict'
import Realisatie from '@components/Realisatie'
import { formatDate, subtractWeeks } from '@util/date-util'
import { getWeeksBeforeStartUitvoering } from '@util/planningsconflict-util'

import { useAuthenticatedUser } from '../../../../auth/hooks/useAuthenticatedUser'
import type { ActiviteitTemplate } from '../../../../templates/types/ActiviteitTemplate'
import { useSaveEvent } from '../../hooks/useSaveEvent'
import type { Project } from '../../types/Project'
import type { QuickscanQuestion } from '../../types/QuickscanQuestion'
import {
  createQuickscanCombiDisciplines,
  createQuickscanHoofdleidingDisciplines,
} from '../../util'

const dateFormat = 'dd-MM-yyyy'
const HOOFDLEIDING_ONLY_WHEN_EXTRA_WVB = ['Vitens']

interface Props {
  project: Project
  handleClose: () => void
  activiteitTemplates: ActiviteitTemplate[]
}

export const Quickscan = ({
  project,
  handleClose,
  activiteitTemplates,
}: Props) => {
  const { mutate: saveEvent } = useSaveEvent()

  const {
    aanvraagID,
    geplandeUitvoeringsdatum,
    geplandeUitvoeringsdatumType,
    combi,
  } = project

  const weeksBeforeStartUitvoering = Math.abs(
    getWeeksBeforeStartUitvoering(combi, activiteitTemplates),
  )
  const { deelnemer, organisatie } = useAuthenticatedUser()

  const [extraWVB, setExtraWVB] = useState('Nee')

  const [extraWVBText, setExtraWVBText] = useState('')
  const [extraWVBDueDate, setExtraWVBDueDate] = useState(
    geplandeUitvoeringsdatum ? new Date(geplandeUitvoeringsdatum) : null,
  )

  const [errors, setErrors] = useState({})
  const [dvErrors, setDvErrors] = useState<Record<string, string>>({})

  const isConditionalHoofdleiding =
    !!deelnemer && HOOFDLEIDING_ONLY_WHEN_EXTRA_WVB.includes(deelnemer)

  const showHoofdleiding = !isConditionalHoofdleiding || extraWVB === 'Ja'

  const disciplineVerzamelingen = project.disciplineVerzamelingen.filter(
    (dv) => dv.netbeheerderLabel === organisatie,
  )

  const [discVer, setDiscVer] = useState(disciplineVerzamelingen)

  const handleChangeExtraWVB = (newValue) => {
    if (newValue !== null) {
      setExtraWVB(newValue)

      if (isConditionalHoofdleiding) {
        setDiscVer(
          project.disciplineVerzamelingen.filter(
            (dv) => dv.netbeheerderLabel === organisatie,
          ),
        )
      }
    }
  }

  const validate = () => {
    const extraWVBTextError =
      (!extraWVBText || extraWVBText === '') && extraWVB === 'Ja'
    const extraWVBDueDateError = !extraWVBDueDate && extraWVB === 'Ja'

    const dvErrors = {}
    setDvErrors(dvErrors)

    for (const dv of discVer) {
      if (!dv.isCombiUitvoering && dv.isCombiUitvoeringReden === undefined) {
        dvErrors[
          `${dv.disciplineCode}-${dv.netbeheerderCode}-combiuitvoering`
        ] = 'Reden is verplicht bij combi uitvoering'
      }
      if (dv.isHoofdleidingAanleggen && !dv.isHoofdleidingAanleggenText) {
        dvErrors[
          `${dv.disciplineCode}-${dv.netbeheerderCode}-hoofdleiding_aanleggen`
        ] = 'Tekst is verplicht bij hoofdleiding aanleggen'
      }
    }

    setDvErrors(dvErrors)

    setErrors({
      extraWVBTextError,
      extraWVBDueDateError,
    })

    return (
      !extraWVBTextError &&
      !extraWVBDueDateError &&
      Object.keys(dvErrors).length === 0
    )
  }

  const handleClickOpslaan = () => {
    const isValid = validate()

    if (isValid) {
      const extraWerkvoorbereiding: QuickscanQuestion = {
        id: 'Extra werkvoorbereiding',
        answer: false,
      }

      if (extraWVB === 'Ja') {
        extraWerkvoorbereiding.text = extraWVBText
        extraWerkvoorbereiding.dueDate =
          subtractWeeks(extraWVBDueDate, weeksBeforeStartUitvoering) ||
          /* v8 ignore next - typing in date util is wrong, not testable -- @preserve */
          undefined
        extraWerkvoorbereiding.answer = true
      }

      const hoofdleidingAanleggen: QuickscanQuestion = {
        id: 'hoofdleiding_aanleggen',
      }

      if (extraWVB === 'Nee' && isConditionalHoofdleiding) {
        hoofdleidingAanleggen.answer = 'unanswered'
      } else {
        hoofdleidingAanleggen.hoofdleidingAanleggenDisciplines =
          createQuickscanHoofdleidingDisciplines(discVer)
      }

      saveEvent(
        {
          id: aanvraagID,
          type: 'quickscan',
          source: 'manual',
          data: {
            questions: [
              {
                id: 'combiuitvoering',
                disciplines: createQuickscanCombiDisciplines(discVer),
              },
              extraWerkvoorbereiding,
              hoofdleidingAanleggen,
            ],
          },
        },
        {
          onSuccess: () => handleClose(),
        },
      )
    }
  }

  return (
    <>
      <DialogTitle>Quickscan</DialogTitle>
      <DialogContent>
        <Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <EditCombiUitvoeringPerDiscipline
                disciplineVerzamelingen={discVer}
                onChange={setDiscVer}
                redenErrors={dvErrors}
              />

              <Accordion
                expanded={extraWVB === 'Ja'}
                slotProps={{ transition: { unmountOnExit: true } }}
              >
                <AccordionSummary component={'div'}>
                  <Typography style={{ flex: 1 }}>
                    Extra werkvoorbereiding
                  </Typography>

                  <BooleanToggleGroup
                    value={extraWVB === 'Ja' ? true : false}
                    onChange={(value) =>
                      handleChangeExtraWVB(value === true ? 'Ja' : 'Nee')
                    }
                  />
                </AccordionSummary>

                <AccordionDetails>
                  {extraWVBDueDate && (
                    <PlanningConflict
                      geplandeUitvoeringsdatum={
                        geplandeUitvoeringsdatum as string
                      }
                      geplandeUitvoeringsdatumType={
                        geplandeUitvoeringsdatumType as string
                      }
                      opleverdatum={subtractWeeks(
                        extraWVBDueDate,
                        weeksBeforeStartUitvoering,
                      )}
                      combi={combi as string}
                      templates={activiteitTemplates}
                    />
                  )}

                  <Typography>
                    Voor deze aanvraag doet u zelf de werkvoorbereiding. Geef
                    aub de start uitvoering datum en de reden aan.
                  </Typography>

                  <Grid
                    container
                    spacing={2}
                    sx={{
                      alignItems: 'center',
                      pt: 2,
                      pb: 2,
                    }}
                  >
                    <Grid>
                      <Typography>
                        De huidige geplande start uitvoering is:{' '}
                      </Typography>
                    </Grid>

                    <Grid>
                      <Realisatie
                        date={geplandeUitvoeringsdatum as string}
                        type={geplandeUitvoeringsdatumType}
                      />
                    </Grid>
                  </Grid>

                  <Grid container sx={{ m: 1 }}>
                    <Grid size={{ xs: 6 }} sx={{ pl: 1 }}>
                      {!isNaN(new Date(extraWVBDueDate as any) as any) &&
                        extraWVBDueDate !== null && (
                          <TextField
                            label="Berekende opleverdatum"
                            variant="standard"
                            value={formatDate(
                              subtractWeeks(
                                extraWVBDueDate,
                                weeksBeforeStartUitvoering,
                              ),
                            )}
                            disabled
                          />
                        )}
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <FormControl>
                        <DatePicker
                          format={dateFormat}
                          label="Start uitvoering"
                          disablePast
                          value={extraWVBDueDate}
                          onChange={setExtraWVBDueDate}
                          displayWeekNumber
                          slotProps={{
                            field: { clearable: true },
                            textField: {
                              error: errors['extraWVBDueDateError'],
                            },
                          }}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>

                  <FormControl style={{ width: '100%', marginTop: 10 }}>
                    <TextField
                      value={extraWVBText}
                      multiline
                      rows={6}
                      label={`Tekst (${extraWVBText.length}/500)`}
                      onChange={(e) => setExtraWVBText(e.target.value)}
                      required
                      error={errors['extraWVBTextError']}
                      slotProps={{ htmlInput: { maxLength: 500 } }}
                    />
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              {showHoofdleiding && (
                <EditHoofdleidingPerDiscipline
                  disciplineVerzamelingen={discVer}
                  onChange={setDiscVer}
                  dvErrors={dvErrors}
                />
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} autoFocus>
          Annuleren
        </Button>
        <Button onClick={handleClickOpslaan}>Opslaan</Button>
      </DialogActions>
    </>
  )
}
